import CropSquareIcon from "@mui/icons-material/CropSquare";
import IndeterminateCheckBoxSharpIcon from "@mui/icons-material/IndeterminateCheckBoxSharp";
import SquareIcon from "@mui/icons-material/Square";
import { Box, Grid, styled, useTheme } from "@mui/material";
import { IDisplayPairing } from "@vmosolutions/oc-interfaces";
import _ from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ToggleGroup } from "src/components/MUI5FormikComponents/ToggleGroup";
import { IPairingMap, oneDayMillis } from "src/pages/CrewSchedule/gl/WaterfallGL";
import { TimelineTime } from "src/pages/FlightTimeline/TimelineTime";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { v1 } from "uuid";
import { PairingWaterfallCanvas } from "./PairingWaterfallCanvas";
import { IRowItem, PairingWaterfallList } from "./PairingWaterfallList";

interface IFilterValues {
  [key: string]: "filled" | "na" | "unfilled";
}
interface IPairingWaterfallWindowProps {
  id: string;
  path: string[];
  windowProps: {
    biddingAssignmentMode: true;
    filter?: IFilterValues;
  };
}

const OCPairingWaterfallWindow = styled(Grid)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexGrow: 1,
  flexBasis: 0,
  flexDirection: "column",
  "& .waterfall_body": {
    width: "calc(100% - 5px)",
    margin: "0px 5px 5px 0px",
    display: "flex",
    flexGrow: 1,
    overflow: "hidden",
    "& .item_list": {
      width: "120px",
    },
  },
  "& .linked": {
    position: "absolute",
    zIndex: 100, // has to be set to under 1200 so that context menu opens above it.
    width: 24,
    height: 24,
    top: 0,
    right: 0,
    borderRadius: 5,
    alignItems: "center",
    overflow: "clip",
    "& .link_button": {
      padding: 0,
      borderRadius: 5,
      "& svg": {
        margin: "0px 6px",
        width: 14,
      },
    },
  },
}));

interface IWaterfallData {
  webglData: IPairingMap;
  itemList: IRowItem[];
  startTime: number;
  dataset_id: string;
}

const defaultData: IWaterfallData = {
  itemList: [],
  webglData: {},
  startTime: new Date()?.valueOf(),
  dataset_id: undefined,
};

/**
 *
 * @param pairingArray - Array of IDisplayPairing or IPairing objects
 * @returns - an object of pairings mapped by pairing id (to avoid duplicates);
 */
const createWaterfallData = (pairingArray: IDisplayPairing[]): IWaterfallData | null => {
  if (!pairingArray?.length) {
    return null;
  }
  // Sort all pairings by their start time then end time.
  const sortedPairings = pairingArray.sort(
    (a, b) => a.start?.valueOf() - b.start?.valueOf() || a.end?.valueOf() - b.end?.valueOf()
  );
  // Create pairing map (by id) using the array of included pairings - ensures no duplicates
  const webglData = sortedPairings.reduce((obj: { [key: string]: any }, pairing) => {
    if (!obj[pairing._id]) {
      obj[pairing._id] = {
        lastModified: new Date()?.valueOf(),
        pairings: [pairing],
      };
    }
    return obj;
  }, {});
  // Item list which displayed on the left of the webgl component (ShortCodes)
  const itemList: IRowItem[] = sortedPairings.map((pairing) => ({
    label: pairing.shortId,
    _id: pairing._id,
    isSelected: false,
    isExpanded: false,
    lastModified: new Date()?.valueOf(),
    startDate: pairing.start?.valueOf(),
    endDate: pairing.end?.valueOf(),
    positions: pairing.positions,
  }));
  // Start time of first pairing found
  const startTime = sortedPairings[0].start?.valueOf();
  const dataset_id = `waterfall-${v1()}`; // assign a new id anytime data is regenerated to force a remount.
  return { webglData, itemList, startTime, dataset_id };
};

// Used to force a list-only remount when filters change.
// (Deliberately not used to remount the WebGL canvas.)
const useListIterationKey = (): [number, () => void] => {
  const [iteration, setIteration] = useState(0);
  const bump = useCallback(() => setIteration((prev) => prev + 1), []);
  return [iteration, bump];
};

export const PairingWaterfallWindow = (props: IPairingWaterfallWindowProps): JSX.Element => {
  const { id, windowProps } = props;
  const positions = useSelector((state: IRootState) => state.structures.customerStructure.positions);
  const defaultPositions: IFilterValues = useMemo(() => {
    const defaultPositionSettings: IFilterValues = {};
    positions.forEach((position) => {
      defaultPositionSettings[position.title] = "na";
    });
    if (windowProps.hasOwnProperty("filter")) {
      Object.keys(windowProps.filter).forEach((filter) => {
        defaultPositionSettings[filter] = windowProps.filter[filter];
      });
    }
    return defaultPositionSettings;
  }, [positions, windowProps]);

  const [positionFilter, setPositionFilter] = useState<IFilterValues>(defaultPositions);
  const biddingAssignmentMode = windowProps?.biddingAssignmentMode;
  const timelineContainer = useRef<HTMLDivElement>(null);
  const crewSchedContainer = useRef<HTMLDivElement>();
  const itemListRef = useRef<HTMLDivElement>();

  // Keep waterfall pairings in their own redux array.
  const waterfallPairings = useSelector((state: IRootState) => state.crewSchedule.waterfallWindow.pairings);
  const waterfallData = useMemo(() => createWaterfallData(waterfallPairings), [waterfallPairings]);

  // function set in ChrewScheduleCanvas, used in TimelineTime to jump to current Time
  const [scrollToDate, setScrollToDate] = useState<(jumpDate: Date) => void>(null);
  const [currentRange, setCurrentRange] = useState<number[]>();
  const [iteration, bumpIteration] = useListIterationKey();

  const { itemList, webglData, startTime } = waterfallData ?? defaultData;
  const filteredItemList = useMemo(() => {
    //Check to see if we even need to filter first, saves time if not.
    if (Object.values(positionFilter).filter((pos) => pos === "na").length === positions.length) {
      return itemList;
    } else {
      return itemList.filter((item) => {
        return Object.entries(positionFilter).some(([key, value]) => {
          //need to handle multiple positions per pairing
          const position = item.positions.filter((pos) => pos.position === key);
          const matchesFilter = position.some((pos) => {
            if (value === "filled") return pos.hasOwnProperty("crew_id");
            if (value === "unfilled") return !pos.hasOwnProperty("crew_id");
            return false;
          });
          return matchesFilter;
        });
      });
    }
  }, [itemList, positionFilter, positions.length]);
  const iconStyle = {
    fontSize: "10px",
    m: "2px",
  };
  const theme = useTheme();
  const filterLabelStyle = {
    cursor: "pointer",
    textAlign: "right",
    color: theme.palette.text.primary,
    fontSize: "0.6rem",
    lineHeight: 1.6,
  };

  const setAllFilters = (filterValue: "filled" | "na" | "unfilled"): void => {
    const newFilters: IFilterValues = Object.keys(positionFilter).reduce((position, key) => {
      position[key] = filterValue;
      return position;
    }, {} as IFilterValues);
    bumpIteration();
    setPositionFilter(newFilters);
  };
  return (
    <OCPairingWaterfallWindow id={`${id}-waterfallWrapper`} ref={timelineContainer} container>
      <Grid
        container
        spacing={0}
        sx={(theme) => ({ px: 1, backgroundColor: theme.palette.divider, alignItems: "center", display: "flex" })}
        justifyContent="start"
        alignItems="center"
      >
        <Grid item sx={{ width: "120px", fontWeight: 900, mr: 2, my: 0.3 }}>
          Pairing Filters:
        </Grid>
        <Grid item xs sx={{ display: "flex", alignItems: "center", justifyContent: "start" }}>
          <Box
            sx={{
              mr: 0.6,
              mt: "18px",
              alignItems: "end",
              display: "flex",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <Box sx={{ ...filterLabelStyle }} onClick={() => setAllFilters("filled")}>
              Filled
            </Box>
            <Box sx={{ ...filterLabelStyle }} onClick={() => setAllFilters("na")}>
              Not Applied
            </Box>
            <Box sx={{ ...filterLabelStyle }} onClick={() => setAllFilters("unfilled")}>
              UnFilled
            </Box>
          </Box>
          {positions.map((position) => (
            <Box key={position.title}>
              <Box
                sx={{
                  width: "100%",
                  fontSize: "0.6rem",
                  display: "flex",
                  justifyContent: "center",
                  top: "4px",
                  position: "relative",
                }}
              >
                {position.title}
              </Box>
              <ToggleGroup
                label=""
                orientation="vertical"
                name={`filter.positions.${position.title}`}
                allowNull={false}
                exclusive={true}
                labelSX={{ "& label": { fontSize: "0.6rem", wordWrap: "no-wrap" } }}
                buttonSX={{ fontSize: "0.7rem", py: 0 }}
                size="small"
                value={positionFilter[position.title]}
                onChange={(event, newValue) => {
                  const newFilter = { ...positionFilter };
                  newFilter[position.title] = newValue;
                  bumpIteration();
                  setPositionFilter(newFilter);
                }}
                options={[
                  { label: <SquareIcon sx={iconStyle} />, value: "filled" },
                  { label: <IndeterminateCheckBoxSharpIcon sx={iconStyle} />, value: "na" },
                  { label: <CropSquareIcon sx={iconStyle} />, value: "unfilled" },
                ]}
              />
            </Box>
          ))}
          <Box
            sx={(theme) => ({
              ml: 2,
              fontSize: "0.6rem",
              lineHeight: 1.6,
              color: theme.palette.text.primary,
              alignItems: "end",
              display: "flex",
            })}
          >
            {itemList.length} pairings
            <br />
            {filteredItemList.length} match filters
          </Box>
        </Grid>
      </Grid>
      <Grid id={`${id}-waterfallBody`} className="waterfall_body" ref={crewSchedContainer}>
        <Grid id={`${id}-itemList`} item className="item_list" style={{ width: biddingAssignmentMode ? 200 : 140 }}>
          <Grid container direction="column" style={{ height: "100%" }}>
            <Grid item style={{ padding: "0px 10px" }}>
              <TimelineTime
                container={crewSchedContainer?.current}
                allowTimezoneControl={true}
                windowId={id}
                setFocusOnDate={scrollToDate}
              />
            </Grid>
            <PairingWaterfallList
              key={iteration}
              itemList={filteredItemList}
              ref={itemListRef}
              id={id}
              setFocusOnDate={scrollToDate}
              setCurrentRange={setCurrentRange}
            />
          </Grid>
        </Grid>

        <PairingWaterfallCanvas
          {...props}
          itemList={filteredItemList}
          itemListRef={itemListRef}
          pairingMap={webglData}
          initialStartTime={startTime - oneDayMillis / 2}
          initialEndTime={startTime + oneDayMillis * 30}
          setCurrentRange={setCurrentRange}
          currentRange={currentRange}
          setScrollToDate={setScrollToDate}
        />
      </Grid>
    </OCPairingWaterfallWindow>
  );
};
